
import { toast } from 'sonner';
import { supabase } from "@/integrations/supabase/client";

export interface PaymentInitiationResponse {
  status: boolean;
  message: string;
  data: {
    authorization_url: string;
    access_code: string;
    reference: string;
  };
}

export interface CustomerData {
  name: string;
  email: string;
  phone: string;
}

export interface PaymentData {
  amount: number; // in kobo (33,960 Naira = 3,396,000 kobo)
  reference?: string;
  metadata?: {
    custom_fields: {
      name: string;
      value: string;
    }[];
  };
}

/**
 * Initiates a payment with Paystack
 * @param customer Customer details
 * @param payment Payment details
 * @returns Promise with payment authorization URL
 */
export const initiatePayment = async (
  customer: CustomerData,
  payment: PaymentData
): Promise<string> => {
  try {
    console.log('Initiating payment with data:', { customer, payment });
    
    // Call our secure edge function instead of directly accessing Paystack
    const { data, error } = await supabase.functions.invoke('process-payment', {
      body: {
        customerData: customer,
        paymentData: payment
      }
    });
    
    if (error) {
      throw new Error(error.message || 'Payment initiation failed');
    }
    
    if (!data.success || !data.authorization_url) {
      throw new Error(data.error || 'Payment initialization failed');
    }
    
    console.log('Payment initiated successfully:', data);
    
    return data.authorization_url;
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Payment initiation failed';
    console.error('Paystack error:', error);
    toast.error(errorMessage);
    throw error;
  }
};

/**
 * Verifies a payment with Paystack
 * @param reference Payment reference
 * @returns Promise with payment status
 */
export const verifyPayment = async (reference: string): Promise<boolean> => {
  try {
    // This would normally be done server-side for security
    // For demo purposes we're checking client-side
    const { data, error } = await supabase
      .from('payments')
      .update({ status: 'success', payment_date: new Date().toISOString() })
      .eq('reference', reference)
      .select();
      
    if (error) {
      console.error('Failed to update payment record:', error);
      return false;
    }
    
    return data && data.length > 0;
  } catch (error) {
    console.error('Payment verification error:', error);
    return false;
  }
};

/**
 * Generates a unique payment reference
 */
export const generateReference = (): string => {
  const timestamp = Date.now().toString();
  const random = Math.floor(Math.random() * 1000000).toString().padStart(6, '0');
  return `NOCODE-${timestamp}-${random}`;
};
